import { CSSProperties, ReactNode } from 'react';
import { ActionIcon, Center, Checkbox, Tooltip } from '@mantine/core';
import { DataField } from '@/types/automationFields.ts';
import StyledMultiSelect from '@/components/StyledInputFields/StyledMultiSelect.tsx';
import ExpandingInputCell from '../ExpandingInputCell.tsx';
import MissingValueCell from './MissingValueCell.tsx';
import DataFieldTypeCell from './DataFieldTypeCell.tsx';
import { HiOutlineTrash } from 'react-icons/hi2';

export interface DataFieldColumnDef {
  header: string;
  styles?: CSSProperties;
  cell: (props: {
    row: DataField;
    update: (field: Partial<DataField>) => void;
    allRows: DataField[];
    deleteRow: () => void;
  }) => ReactNode;
  width?: string;
}

export const dataFieldTableColumns: DataFieldColumnDef[] = [
  {
    header: 'Name',
    styles: { minWidth: '200px' },
    cell: ({ row, update }) => (
      <ExpandingInputCell
        value={row.name || ''}
        header={'Name'}
        placeholder="name"
        onChange={(event) => update({ name: event.currentTarget.value })}
      />
    ),
  },
  {
    header: 'Type',
    cell: ({ row, update }) => <DataFieldTypeCell field={row} update={update} />,
  },

  {
    header: 'Description',
    cell: ({ row, update }) => (
      <ExpandingInputCell
        value={row.description || ''}
        placeholder="description"
        onChange={(event) => update({ description: event.currentTarget.value })}
        header={'Description'}
      />
    ),
  },
  {
    header: 'Required',
    cell: ({ row, update }) => (
      <Center>
        <Checkbox
          size={'xs'}
          checked={row.is_required}
          onChange={(event) => {
            const is_required = event.currentTarget.checked;
            update({
              is_required,
              // Required fields cannot have missing-value or not-applicable policies.
              ...(is_required
                ? {
                    default_value: null,
                    missing_value: null,
                    ...(row.not_applicable_policy ? { not_applicable_policy: null } : {}),
                  }
                : {}),
            });
          }}
        />
      </Center>
    ),
  },
  {
    header: 'When missing',
    cell: ({ row, update }) => (
      <MissingValueCell field={row} update={update} />
    ),
  },
  {
    header: 'Not applicable when',
    styles: { minWidth: '220px' },
    cell: ({ row, update }) => (
      <ExpandingInputCell
        value={row.not_applicable_policy?.condition || ''}
        header="Not Applicable When"
        inputAriaLabel="Not applicable when"
        placeholder="source-backed condition"
        disabled={row.is_required}
        onChange={(event) => {
          const condition = event.currentTarget.value;
          update({
            not_applicable_policy: condition.trim() ? { condition } : null,
          });
        }}
      />
    ),
  },

  {
    header: 'Formatting rules',
    cell: ({ row, update }) => (
      <ExpandingInputCell
        value={row.formatting_rules || ''}
        placeholder="formatting rules"
        onChange={(event) => update({ formatting_rules: event.currentTarget.value })}
        header={'Formatting Rules'}
      />
    ),
  },
  {
    header: 'Blank source = 0',
    cell: ({ row, update }) => (
      <Center>
        {row.field_type === 'Number' && (
          <Tooltip
            label="Use only when a genuinely blank source cell means zero. Do not use for unavailable or not-applicable values."
            multiline
            w={260}
            withArrow
          >
            <Checkbox
              size={'xs'}
              aria-label="Treat blank source cell as zero"
              checked={Boolean(row.blank_as_zero_allowed)}
              onChange={(event) => update({ blank_as_zero_allowed: event.currentTarget.checked })}
            />
          </Tooltip>
        )}
      </Center>
    ),
  },
  {
    header: 'Dependencies',
    styles: { maxWidth: '200px' },
    cell: ({ row, update, allRows }) => {
      const options = allRows
        .filter((f) => !!f.name && f.name !== row.name)
        .map((f) => ({ value: f.field_id, label: f.name! }));

      const value = options.filter((o) => row.dependencies.includes(o.value)).map((o) => o.value);

      return (
        <StyledMultiSelect
          value={value}
          data={options}
          onChange={(value) => update({ dependencies: value })}
          hidePickedOptions={true}
          // label="Dependencies"
          // placeholder="Select an existing automation"
        />
      );
    },
  },
  {
    header: 'Internet Access',
    cell: ({ row, update }) => (
      <Center>
        <Checkbox
          size={'xs'}
          checked={row.internet_access}
          onChange={() => update({ internet_access: !row.internet_access })}
        />
      </Center>
    ),
  },
  {
    header: '',
    cell: ({ deleteRow }) => (
      <Center>
        <ActionIcon variant={'subtle'} color={'gray'} onClick={() => deleteRow()}>
          <HiOutlineTrash size={16} />
        </ActionIcon>
        {/*<Checkbox checked={false} onChange={() => {}} size="xs" />*/}
      </Center>
    ),
  },
];
